import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Typography,
} from "@mui/material";

interface AddBatchExpiryDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: { quantity: number; expiry_date: string }) => Promise<void>;
    productName: string;
    maxQuantity: number;
}

const defaultForm = (maxQuantity: number) => ({
    quantity: maxQuantity,
    expiry_date: "",
});

export const AddBatchExpiryDialog: React.FC<AddBatchExpiryDialogProps> = ({
    open,
    onClose,
    onConfirm,
    productName,
    maxQuantity,
}) => {
    const [form, setForm] = useState(() => defaultForm(maxQuantity));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleOpen = () => {
        setForm(defaultForm(maxQuantity));
        setError(null);
    };

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!form.expiry_date) {
            setError("Expiry date is required.");
            return;
        }
        if (form.quantity <= 0) {
            setError("Quantity must be greater than 0.");
            return;
        }
        if (form.quantity > maxQuantity) {
            setError(`Quantity can't exceed the ${maxQuantity} units received.`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onConfirm({
                quantity: form.quantity,
                expiry_date: `${form.expiry_date}T00:00:00`,
            });
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.data?.detail ||
                    err?.message ||
                    "Failed to add expiry date. Please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionProps={{ onEnter: handleOpen }}
        >
            <DialogTitle>Add Expiry Date</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {productName}
                    </Typography>
                    <TextField
                        label="Quantity"
                        type="number"
                        value={form.quantity}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                quantity: Number(e.target.value),
                            })
                        }
                        fullWidth
                        required
                        inputProps={{ min: 1, max: maxQuantity }}
                        helperText={`How many of the ${maxQuantity} received units expire on this date`}
                    />
                    <TextField
                        label="Expiry Date"
                        type="date"
                        value={form.expiry_date}
                        onChange={(e) =>
                            setForm({ ...form, expiry_date: e.target.value })
                        }
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={saving}
                    startIcon={
                        saving ? <CircularProgress size={16} /> : undefined
                    }
                >
                    {saving ? "Saving..." : "Add Expiry"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddBatchExpiryDialog;
